import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, signUp, resetPassword } from "../services/authService";
import { useAuth } from "../hooks/useAuth";

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | register | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [credentials, setCredentials] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, homeRoute, profile } = useAuth();

  // Redirect when useAuth confirms profile is loaded
  useEffect(() => {
    if (isAuthenticated && profile) {
      navigate(homeRoute, { replace: true });
    }
  }, [isAuthenticated, profile, homeRoute, navigate]);

  const handleLogin = async () => {
    if (!email || !password) return;
    setError("");
    setLoading(true);
    const result = await signIn(email, password);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
    // Don't navigate here — useEffect above handles it after profile loads
  };

  const handleRegister = async () => {
    if (!email || !password || !firstName || !lastName || !inviteCode.trim()) return;
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setError("");
    setLoading(true);
    const result = await signUp({
      email,
      password,
      firstName,
      lastName,
      credentials,
      employeeId,
      inviteCode: inviteCode.trim().toUpperCase(),
    });
    if (!result.success) {
      setError(result.error);
      setLoading(false);
    }
    // Don't navigate here — useEffect above handles it after profile loads
  };

  const handleReset = async () => {
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setError("");
    setLoading(true);
    const result = await resetPassword(email);
    if (result.success) {
      setSuccess("Password reset email sent. Check your inbox.");
      setTimeout(() => { setMode("login"); setSuccess(""); }, 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  return (
    <div className="min-h-screen bg-surface-primary flex flex-col justify-center px-8">
      {/* Logo */}
      <div className="text-center mb-10 animate-fade-in">
        <div className="text-xs font-bold tracking-[3px] uppercase text-accent-teal mb-3">
          Cold Harbor Digital
        </div>
        <h1
          className="text-4xl font-extrabold tracking-tight leading-tight mb-2"
          style={{
            background: "linear-gradient(135deg, #63b3ed, #4fd1c5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          FieldNotes
        </h1>
        <p className="text-sm text-gray-500">AI-Powered Clinical Documentation</p>
      </div>

      <div className="animate-slide-up" style={{ animationDelay: "0.1s" }}>
        {/* Error / Success */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
            {success}
          </div>
        )}

        {/* ===== LOGIN ===== */}
        {mode === "login" && (
          <div className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="field-input"
              autoComplete="email"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="field-input"
              autoComplete="current-password"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />

            <button
              onClick={handleLogin}
              disabled={loading || !email || !password}
              className="w-full py-4 rounded-xl text-white font-semibold text-base transition-all"
              style={{
                background: loading ? "rgba(49,130,206,0.5)" : "linear-gradient(135deg, #3182ce, #2b6cb0)",
                opacity: !email || !password ? 0.5 : 1,
              }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>

            <div className="flex justify-between items-center pt-2">
              <button
                className="text-xs text-gray-500 hover:text-blue-400 transition-colors"
                onClick={() => switchMode("reset")}
              >
                Forgot password?
              </button>
              <button
                className="text-xs text-blue-400 font-semibold"
                onClick={() => switchMode("register")}
              >
                Create Account
              </button>
            </div>
          </div>
        )}

        {/* ===== REGISTER ===== */}
        {mode === "register" && (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-gray-300 mb-1">Create Account</div>

            {/* Organization Invite Code — required */}
            <div>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Organization Invite Code *"
                className="field-input"
                autoComplete="off"
                style={{ letterSpacing: 2, textTransform: "uppercase" }}
              />
              <div className="text-[10px] text-gray-600 mt-1 ml-1">
                Get this from your supervisor or administrator
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name *"
                className="field-input"
                autoComplete="given-name"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name *"
                className="field-input"
                autoComplete="family-name"
              />
            </div>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email *"
              className="field-input"
              autoComplete="email"
            />

            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
                placeholder="Credentials (e.g. LPC)"
                className="field-input"
              />
              <input
                type="text"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="Employee ID"
                className="field-input"
              />
            </div>

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password * (min 6 characters)"
              className="field-input"
              autoComplete="new-password"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password *"
              className="field-input"
              autoComplete="new-password"
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
            />

            {/* Role info */}
            <div
              className="rounded-xl px-4 py-3"
              style={{
                background: "rgba(99,179,237,0.04)",
                border: "1px solid rgba(99,179,237,0.08)",
              }}
            >
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                Account Role
              </div>
              <div className="text-xs text-gray-400">
                All new accounts are created as <span className="text-blue-400 font-semibold">Community Agent</span>.
                Your supervisor can update your role after you join.
              </div>
            </div>

            <button
              onClick={handleRegister}
              disabled={loading || !email || !password || !firstName || !lastName || !confirmPassword || !inviteCode.trim()}
              className="w-full py-4 rounded-xl text-white font-semibold text-base transition-all"
              style={{
                background: loading
                  ? "rgba(49,130,206,0.5)"
                  : "linear-gradient(135deg, #3182ce, #2b6cb0)",
                opacity: !email || !password || !firstName || !lastName || !confirmPassword || !inviteCode.trim() ? 0.5 : 1,
              }}
            >
              {loading ? "Creating Account..." : "Join Organization"}
            </button>

            <div className="text-center pt-2">
              <button
                className="text-xs text-gray-500"
                onClick={() => switchMode("login")}
              >
                Already have an account? <span className="text-blue-400 font-semibold">Sign In</span>
              </button>
            </div>
          </div>
        )}

        {/* ===== RESET PASSWORD ===== */}
        {mode === "reset" && (
          <div className="space-y-3">
            <div className="text-sm font-semibold text-gray-300 mb-1">Reset Password</div>
            <p className="text-xs text-gray-500 mb-2">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="field-input"
              autoComplete="email"
              onKeyDown={(e) => e.key === "Enter" && handleReset()}
            />

            <button
              onClick={handleReset}
              disabled={loading || !email}
              className="w-full py-4 rounded-xl text-white font-semibold text-base transition-all"
              style={{
                background: loading ? "rgba(49,130,206,0.5)" : "linear-gradient(135deg, #3182ce, #2b6cb0)",
                opacity: !email ? 0.5 : 1,
              }}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="text-center pt-2">
              <button
                className="text-xs text-gray-500"
                onClick={() => switchMode("login")}
              >
                Back to <span className="text-blue-400 font-semibold">Sign In</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-[10px] text-gray-700 tracking-wide">
        HIPAA Compliant · AES-256 Encryption
      </div>
    </div>
  );
}
